import { useCallback, useMemo, useRef, useState, type MouseEvent } from 'react';
import { useMountEffect } from '@/shared/hooks/useMountEffect';
import clsx from 'clsx';
import { Info } from 'lucide-react';
import { getWidgetStores, InfoMenu, InfoMenuInput, InfoMenuType } from '@/entities/widget';
import { logger } from '@/lib/utils/logger';
import { SidePanel } from '@/shared/ui/SidePanelWrapper';
import { useSaveToProjectAction } from '@/shared/ui';
import { downloadFile } from '@/shared/utils/downloadFile';
import { default as normalizeVersions } from '../lib/image-widget.utils';
import {
  type ImageGenerationWidgetImage,
  type ImageGenerationWidgetProps,
  type ImageWidgetFileAsset,
} from '../model/image-widget.types';
import ImageCard from './components/ImageCard/ImageCard';
import ImageEditor from './components/ImageEditor/ImageEditor';
import { EditTool } from './components/ImageEditor/ImageEditor.types';
import ImageMask from './components/ImageMask/ImageMask';
import { ImageMaskHandle } from './components/ImageMask/ImageMask.types';
import { ToggleGroup, ToggleGroupItem } from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';

export const ImageWidget = (props: ImageGenerationWidgetProps) => {
  const { t } = useTranslation('contentTest');

  // Store + chats access via the widget binding seam.
  // See `entities/widget/lib/widget-store-bindings`. Production wiring at
  // app boot supplies the real `@/app/store/widget` + `@/features/chats`
  // modules; tests stub `getWidgetStores()` at the seam.
  const { widgetStore, chatStore } = getWidgetStores();
  const {
    useChatStore,
    useChatStoreInput,
    useMessages,
    useUpdateAgentState,
    useFileAttachments,
    SUPPORTED_IMAGE_MODELS,
  } = chatStore;

  const data = useMemo(() => normalizeVersions(props.data), [props.data]);

  const images: ImageGenerationWidgetImage[] = useMemo(
    () => (Array.isArray(data.images) ? data.images : []),
    [data.images],
  );

  const firstImage = images[0];

  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [viewIndex, setViewIndex] = useState<number | null>(null);
  const [infoMenuAnchor, setInfoMenuAnchor] = useState<HTMLElement | null>(null);

  // Version selector support
  const { widget } = widgetStore.useWidgetPanel();
  const agentId = widget?.agentName ?? '';
  const versionConfig = widgetStore.useWidgetVersions(agentId);
  const updateAgentState = useUpdateAgentState();

  // "Save to project" — gated on the flag, a workspace, and a live source
  // artifact id (absent in the read-only Library preview, so it hides there).
  const sourceArtifactId = typeof widget?.artifactId === 'string' ? widget.artifactId : undefined;
  // eslint-disable-next-line i18next/no-literal-string -- artifact kind wire enum value
  const saveAction = useSaveToProjectAction('image', sourceArtifactId);

  // Get the currently selected model from chat input options
  const { chatInput } = useChatStoreInput();
  const selectedModel = (chatInput.options as { settings?: { model?: string } } | undefined)
    ?.settings?.model;

  // Check if mask editing is allowed based on the prop bound by the
  // Config-layer wrapper (dependency injection — CRITICAL #3) AND the
  // currently selected model. Defaulting to `true` preserves the
  // historical `agentConfig?.allowImageMaskEditing ?? true` semantics
  // for any consumer that doesn't pass the prop.
  const agentSupportsEditing = props.allowImageMaskEditing ?? true;
  // The Library preview mount is non-editing: never allow mask editing there,
  // regardless of agent/model support.
  const readOnly = props.readOnly ?? false;
  const allowMaskEditing = useMemo(() => {
    if (readOnly) return false;
    if (!agentSupportsEditing) return false;

    // Then check if the currently selected model supports edit-with-mask
    if (!selectedModel) return false;
    const modelConfig = SUPPORTED_IMAGE_MODELS.find((m) => m.value === selectedModel);
    return modelConfig?.features.includes('edit-with-mask') ?? false;
  }, [readOnly, agentSupportsEditing, selectedModel, SUPPORTED_IMAGE_MODELS]);

  const [editing, setEditing] = useState<{ index: number; img: ImageGenerationWidgetImage } | null>(
    null,
  );
  const [tool, setTool] = useState<EditTool>('rect');
  const maskRef = useRef<ImageMaskHandle | null>(null);
  const closingRef = useRef(false);
  // Latch for the "referenced image detached from the composer" close below.
  const pendingCloseKeyRef = useRef<string | null>(null);

  const messages = useMessages();
  const { files: composerFiles, handleFileReference, handleRemoveFile } = useFileAttachments({});

  // (derived-inline): if images identity changes, reset viewIndex.
  const [lastImagesRef, setLastImagesRef] = useState<unknown>(images);
  if (images !== lastImagesRef) {
    setLastImagesRef(images);
    if (viewIndex !== null) setViewIndex(null);
  }

  // Build a lookup map for agent-attached files by assetId.
  // The widget binding exposes messages with a loose shape (`m.files` may
  // be `undefined`); we narrow at use here.
  const filesByAssetId = useMemo(() => {
    const map = new Map<string, ImageWidgetFileAsset>();
    for (const m of messages) {
      if (m.type !== 'agent') continue;
      const files = m.files ?? [];
      for (const f of files) {
        if (f.assetId) map.set(f.assetId, f as unknown as ImageWidgetFileAsset);
      }
    }
    return map;
  }, [messages]);

  // Resolve a ImageWidgetFileAsset to use as the "referenceable" file for an image
  const getRefFileFor = useCallback(
    (img: ImageGenerationWidgetImage): ImageWidgetFileAsset | null => {
      if (!img.assetId) return null;

      const fromStore = filesByAssetId.get(img.assetId);
      if (fromStore) return fromStore;

      return {
        assetId: img.assetId,
        url: img.url ?? '',
        mimeType: img.mimeType ?? 'image/png',
        metadata: {
          fileName: img.metadata.fileName,
          size: img.metadata.size,
        },
      } as ImageWidgetFileAsset;
    },
    [filesByAssetId],
  );

  // Keep exactly one referenced image (by assetId)
  const enforceSingleReference = useCallback(
    (target: ImageWidgetFileAsset) => {
      composerFiles.forEach((f) => {
        if (f.assetId !== target.assetId) handleRemoveFile(f.assetId);
      });

      const already = composerFiles.some((f) => f.assetId === target.assetId);
      if (!already) {
        handleFileReference(target);
      }
    },
    [composerFiles, handleFileReference, handleRemoveFile],
  );

  // Toggle add/remove from composer
  const onToggleReference = useCallback(
    (image: ImageGenerationWidgetImage | null) => {
      if (!image) return;

      const ref = getRefFileFor(image);
      if (!ref) return;

      const attached = composerFiles.some((f) => f.assetId === ref.assetId);

      if (attached) {
        handleRemoveFile(ref.assetId);
      } else {
        enforceSingleReference(ref);
      }
    },
    [composerFiles, getRefFileFor, handleRemoveFile, enforceSingleReference],
  );

  // Always download with a fresh signed URL
  const onDownload = useCallback(async (image: ImageGenerationWidgetImage) => {
    try {
      setDownloadingId(image.assetId);

      if (!image.url) return;

      await downloadFile({
        url: image.url,
        mimeType: image.mimeType,
        fileName: image.metadata.fileName,
      });
    } catch (err) {
      logger.error('Download failed:', err);
    } finally {
      setDownloadingId(null);
    }
  }, []);

  // Info panel content
  const formattedInput = useMemo(() => {
    if (!data.input) return null;
    const parts: InfoMenuInput = [];

    if (data.input.files && data.input.files.length > 0) {
      parts.push({
        [t('imageWidget.infoMenu.referenceImages')]: {
          type: InfoMenuType.Image,
          value: data.input.files,
        },
      });
    }
    if (data.input.prompt) {
      parts.push({
        [t('imageWidget.infoMenu.prompt')]: {
          type: InfoMenuType.LongText,
          value: data.input.prompt,
        },
      });
    }
    if (data.createdAt) {
      const iso = new Date(data.createdAt).toISOString();
      parts.push({ [t('imageWidget.infoMenu.date')]: { type: InfoMenuType.Date, value: iso } });
    }
    parts.push({
      [t('imageWidget.infoMenu.model')]: {
        type: InfoMenuType.Text,
        value: data.input.settings.model,
      },
    });

    if ('customModelId' in data.input.settings && data.input.settings.customModelId) {
      parts.push({
        [t('imageWidget.infoMenu.customModelId')]: {
          type: InfoMenuType.Text,
          value: data.input.settings.customModelId,
        },
      });
    }

    if ('aspectRatio' in data.input.settings && data.input.settings.aspectRatio) {
      parts.push({
        [t('imageWidget.infoMenu.aspectRatio')]: {
          type: InfoMenuType.Text,
          value: data.input.settings.aspectRatio,
        },
      });
    }
    // `ImageGenerationWidgetSettings` doesn't (yet) declare `resolution` on any
    // of its model variants, so the usual `'key' in settings` narrowing used
    // above for aspectRatio/size collapses to an unusable type here. Read it
    // via a structural cast instead — the field is written by models
    // (gemini-3-pro-image, gemini-3.1-flash-image) outside the current type
    // union but is present on the runtime payload.
    const settingsWithResolution = data.input.settings as typeof data.input.settings & {
      resolution?: string;
    };
    if (settingsWithResolution.resolution) {
      parts.push({
        [t('imageWidget.infoMenu.resolution')]: {
          type: InfoMenuType.Text,
          value: settingsWithResolution.resolution,
        },
      });
    }
    if ('size' in data.input.settings && data.input.settings.size) {
      parts.push({
        [t('imageWidget.infoMenu.size')]: {
          type: InfoMenuType.Text,
          value: data.input.settings.size,
        },
      });
    }
    return parts;
  }, [data, t]);

  const setImageMask = useCallback((opts: { mask: string }) => {
    useChatStore.getState().setInputOptions((prev) => {
      const prevObj = typeof prev === 'object' && prev;
      return {
        ...prevObj,
        mask: opts.mask, // Keep base64 while editing
      };
    });
    // `useChatStore` is a Zustand store hook with a permanently stable
    // identity — it does not need to be in the dep array (the eslint rule
    // can't see this guarantee). Same in clearImageMask + the subscription
    // useEffect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMaskChange = useCallback(
    (base64: string) => {
      setImageMask({ mask: base64 || '' });
    },
    [setImageMask],
  );

  const clearImageMask = useCallback(() => {
    useChatStore.getState().setInputOptions((prev) => {
      if (!prev) return undefined;

      if (!Object.prototype.hasOwnProperty.call(prev as object, 'mask')) {
        return prev;
      }

      const next = { ...prev } as Record<string, unknown>;
      delete next.mask;
      return Object.keys(next).length ? next : undefined;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Zustand store hook has stable identity
  }, []);

  // Enter Edit mode
  const openEdit = useCallback(
    (img: ImageGenerationWidgetImage, index: number, refOverride?: ImageWidgetFileAsset | null) => {
      const ref = refOverride ?? getRefFileFor(img);
      if (!ref) return;

      const attached = composerFiles.some((f) => f.assetId === ref.assetId);
      if (!attached) handleFileReference(ref);

      // Re-entering edit mode releases the detachment latch below.
      pendingCloseKeyRef.current = null;
      setViewIndex(index);
      setEditing({ index, img });
      updateAgentState((prev) => ({ ...prev, isDrawingMask: true }));
    },
    [composerFiles, getRefFileFor, handleFileReference, updateAgentState],
  );

  // Exit Edit mode
  const closeEdit = useCallback(() => {
    if (editing) {
      const ref = getRefFileFor(editing.img);
      if (ref) handleRemoveFile(ref.assetId);

      maskRef.current?.clear();
      clearImageMask();
    }
    setEditing(null);
    updateAgentState((prev) => ({ ...prev, isDrawingMask: false }));
  }, [editing, getRefFileFor, handleRemoveFile, clearImageMask, updateAgentState]);

  // (Rule 4 external subscription via `useMountEffect`): close
  // edit mode when the mask is removed or when a send starts. Read latest
  // `editing`/`closeEdit` through refs so the subscription registers exactly
  // once and never re-subscribes.
  const editingRef = useRef(editing);
  editingRef.current = editing;
  const closeEditRef = useRef(closeEdit);
  closeEditRef.current = closeEdit;
  useMountEffect(() => {
    const hasMaskKey = (opts: Record<string, unknown> | undefined) =>
      !!opts && Object.prototype.hasOwnProperty.call(opts, 'mask');

    const unsubscribe = useChatStore.subscribe((state, prev) => {
      if (closingRef.current) return;

      const nowHasMaskKey = hasMaskKey(state.chatInput.options);
      const prevHadMaskKey = hasMaskKey(prev.chatInput.options);
      const justStartedSending = state.aiStatus === 'submitted' && prev.aiStatus !== 'submitted';

      if (!editingRef.current) return;

      if ((prevHadMaskKey && !nowHasMaskKey) || justStartedSending) {
        closingRef.current = true;
        closeEditRef.current();
        setTimeout(() => {
          closingRef.current = false;
        }, 0);
      }
    });

    return unsubscribe;
  });

  // (derived-inline / event-handler): detect a detached reference
  // during render and close edit mode in the same tick. `closeEdit` is NOT
  // idempotent (it removes the composer file, clears the mask and writes agent
  // state), so the scheduling is latched per detected detachment: without the
  // latch every subsequent render — and both passes of a StrictMode render —
  // would enqueue another `closeEdit`. The latch is released by `openEdit`,
  // the only way back into edit mode.
  if (editing) {
    const editingRefFile = getRefFileFor(editing.img);
    const stillAttached =
      !!editingRefFile && composerFiles.some((f) => f.assetId === editingRefFile.assetId);
    const detachKey = `${editing.index}:${editing.img.assetId}`;
    if (!stillAttached && pendingCloseKeyRef.current !== detachKey) {
      pendingCloseKeyRef.current = detachKey;
      queueMicrotask(() => {
        // The render that scheduled this may have been discarded, so re-check
        // against the latest committed state before mutating anything.
        const active = editingRef.current;
        if (!active || `${active.index}:${active.img.assetId}` !== detachKey) return;
        closeEditRef.current();
      });
    }
  }

  if (!props.data) return <div>{t('imageWidget.noDataProvidedForImageWidget')}</div>;

  const title =
    data.title ||
    (images.length > 0 ? firstImage.metadata.fileName : undefined) ||
    t('imageWidget.image');

  // Header actions
  const handleInfoMenuOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setInfoMenuAnchor(event.currentTarget);
  };

  const handleInfoMenuClose = () => {
    setInfoMenuAnchor(null);
  };

  // `renderSingle` closes over ~12 values
  // (getRefFileFor, composerFiles, downloadingId, editing, onToggleReference,
  // onDownload, openEdit, allowMaskEditing, readOnly, maskRef, tool,
  // handleMaskChange) and is called from 2 sites with different arguments
  // (single-image view vs. the filtered/selected view), so it can't be
  // collapsed into a single precomputed value. Extracting it to a sibling
  // component would require threading more props than the closure itself —
  // worse, not better, so it stays local.
  const renderSingle = (img: ImageGenerationWidgetImage, indexOverride?: number) => {
    const idx = indexOverride ?? images.findIndex((i) => i.assetId === img.assetId);
    const refFile = getRefFileFor(img);
    const attached = !!refFile && composerFiles.some((f) => f.assetId === refFile.assetId);
    const isEditingThis = !!editing && editing.index === idx;

    // While masking, keep the natural full-width flow so the ImageMask overlay
    // stays pixel-aligned with the image. Otherwise fit the image to the
    // available panel height so extreme aspect ratios (e.g. 1:8) stay fully
    // visible instead of overflowing/cropping.
    const fit = !isEditingThis;

    return (
      <div
        className={clsx('relative mr-10.5 flex justify-center', fit ? 'flex-1 min-h-0' : 'mt-8')}
      >
        <div className={clsx('relative w-full', fit && 'h-full')} style={{ maxWidth: 768 }}>
          <ImageCard
            img={img}
            resolvedRefFile={refFile}
            isInComposer={attached}
            onToggleReference={(ref) => {
              onToggleReference(ref);
            }}
            onDownload={(ref) => {
              void onDownload(ref);
            }}
            downloading={downloadingId === img.assetId}
            onEdit={(ref) => openEdit(img, idx, ref)}
            isEditDisabled={!allowMaskEditing}
            hideOverlay={!!editing}
            readOnly={readOnly}
            fitToContainer={fit}
          />
          {isEditingThis && <ImageMask ref={maskRef} tool={tool} onMaskChange={handleMaskChange} />}
        </div>
      </div>
    );
  };

  return (
    <SidePanel
      title={title}
      headerConfig={{
        chip: {
          label: t('imageWidget.image'),
          color: 'orange',
        },
        versionConfig,
        actions: [
          {
            type: 'icon',
            icon: <Info size={20} />,
            onClick: handleInfoMenuOpen,
            tooltip: t('imageWidget.viewPromptInfo'),
          },
          ...(saveAction ? [saveAction] : []),
        ],
      }}
    >
      {/* Scrollable content area matching fullscreen-v2 paddings */}
      <div
        className="h-full overflow-y-auto relative flex flex-col"
        style={{
          paddingInlineStart: '60px',
          paddingInlineEnd: '16px',
          paddingBlockEnd: '96px',
        }}
      >
        {(images.length > 1 || !!editing) && (
          <div className={`mt-6 ${editing ? 'sticky top-0 z-10' : ''}`}>
            {editing ? (
              <ImageEditor
                tool={tool}
                setTool={setTool}
                onClear={() => maskRef.current?.clear()}
                onClose={closeEdit}
              />
            ) : (
              <ToggleGroup
                type="single"
                variant="outline"
                size="sm"
                spacing={2}
                // eslint-disable-next-line i18next/no-literal-string -- selection sentinel, not copy
                value={viewIndex === null ? 'all' : String(viewIndex)}
                // A single-value ToggleGroup lets the reader deselect, which would leave
                // no image chosen at all; an empty value falls back to "all".
                onValueChange={(next) => setViewIndex(next && next !== 'all' ? Number(next) : null)}
              >
                {/* eslint-disable-next-line i18next/no-literal-string -- selection sentinel, not copy */}
                <ToggleGroupItem value="all">{t('imageWidget.all')}</ToggleGroupItem>
                {images.map((_, idx) => (
                  <ToggleGroupItem key={`img-tab-${idx}`} value={String(idx)}>
                    {idx + 1}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            )}
          </div>
        )}

        {/* Content */}
        {images.length === 0 ? null : images.length === 1 ? (
          renderSingle(images[0], 0)
        ) : viewIndex === null ? (
          <div className="mt-12 mr-10.5 grid grid-cols-2 gap-6">
            {images.map((img, idx) => (
              <div key={img.assetId} className="relative w-full" style={{ maxWidth: 768 }}>
                <ImageCard
                  img={img}
                  resolvedRefFile={getRefFileFor(img)}
                  isInComposer={composerFiles.some((f) => f.assetId === img.assetId)}
                  onToggleReference={(ref) => {
                    onToggleReference(ref);
                  }}
                  onDownload={(ref) => {
                    void onDownload(ref);
                  }}
                  downloading={downloadingId === img.assetId}
                  onEdit={(ref) => openEdit(img, idx, ref)}
                  isEditDisabled={!allowMaskEditing}
                  hideOverlay={!!editing}
                  readOnly={readOnly}
                />
                {!!editing && editing.index === idx && (
                  <ImageMask ref={maskRef} tool={tool} onMaskChange={handleMaskChange} />
                )}
              </div>
            ))}
          </div>
        ) : (
          renderSingle(images[viewIndex], viewIndex)
        )}
      </div>

      <InfoMenu
        input={formattedInput}
        anchorEl={infoMenuAnchor}
        open={Boolean(infoMenuAnchor)}
        onClose={handleInfoMenuClose}
      />
    </SidePanel>
  );
};
